import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { verifyDocument, saveVerifyLog, type VerifyResult } from '../../shared/api';

const STRIP_B64 = /^data:image\/[a-zA-Z]+;base64,/;

interface UseVerifyOptions {
  frontPhoto?: string;
  backPhoto?:  string;
  options?:    Record<string, unknown>;
}

/**
 * Llama a Microblink Verify cuando las fotos están disponibles y guarda el log.
 * La llamada se ejecuta una sola vez por sesión (evita dobles llamadas en dev).
 */
export function useVerify({ frontPhoto, backPhoto, options = {} }: UseVerifyOptions) {
  const [result, setResult]   = useState<VerifyResult | null>(null);
  const calledRef             = useRef(false); // evita doble ejecución en StrictMode/dev

  const saveLog = useCallback(
    async (responseGotten: string, verifyResponse: unknown, front: string, back: string) => {
      try {
        await saveVerifyLog({ uuid: uuidv4(), responseGotten, verifyResponse, frontImage: front, backImage: back });
      } catch (err) {
        // El log no es crítico; no interrumpir el flujo si falla
        console.warn('[useVerify] saveLog error:', err);
      }
    },
    [],
  );

  useEffect(() => {
    if (!frontPhoto || calledRef.current) return;
    calledRef.current = true;

    const front = frontPhoto.replace(STRIP_B64, '');
    const back  = backPhoto ? backPhoto.replace(STRIP_B64, '') : '';

    verifyDocument({ frontImage: front, backImage: back, options })
      .then((data) => {
        setResult(data);
        saveLog(data.verification?.result ?? 'Sin resultado', data, front, back);
      })
      .catch((err: unknown) => {
        const isNetworkError = err instanceof Error && err.message === 'Network Error';
        if (isNetworkError) {
          console.warn('[useVerify] No se pudo conectar con Microblink Verify (ERR_NAME_NOT_RESOLVED). Verifica la conexión o el endpoint.');
        } else {
          console.warn('[useVerify]', err);
        }
      });
  // options es estático durante la vida del componente; no incluir en deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontPhoto]);

  return result;
}
